import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import AboutMicrogreensPage from "@/pages/AboutMicrogreensPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import EbookPage from "@/pages/PrarohaEbookPage";
import SpotlightPage from "@/pages/SpotlightPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import FAQPage from "@/pages/FAQPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsConditionsPage from "@/pages/TermsConditionsPage";
import ContactPage from "@/pages/ContactPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ProfilePage from "@/pages/ProfilePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// Admin Pages
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminHarvest from "@/pages/admin/AdminHarvest";
import AdminChatAnalytics from "@/pages/admin/AdminChatAnalytics";
import AdminSpotlight from "@/pages/admin/AdminSpotlight";

// Components
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Cart Context
export const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function LoginRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get("redirect") || "/";
  return <Navigate to={redirectTo} replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], total: 0 });

  // Check for existing auth on mount
  useEffect(() => {
    const token = localStorage.getItem("praroha_token");
    const savedUser = localStorage.getItem("praroha_user");
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      fetchCart(token);
    }
    setLoading(false);
  }, []);

  const fetchCart = async (token) => {
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("praroha_token", access_token);
    localStorage.setItem("praroha_user", JSON.stringify(userData));
    setUser(userData);
    fetchCart(access_token);
    return userData;
  };

  const register = async (name, email, password, phone) => {
    const response = await axios.post(`${API}/auth/register`, { 
      name, email, password, phone 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("praroha_token", access_token);
    localStorage.setItem("praroha_user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("praroha_token");
    localStorage.removeItem("praroha_user");
    setUser(null);
    setCart({ items: [], total: 0 });
  };

  // Auto-logout on 401 (expired/invalid token)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && localStorage.getItem("praroha_token")) {
          localStorage.removeItem("praroha_token");
          localStorage.removeItem("praroha_user");
          setUser(null);
          setCart({ items: [], total: 0 });
          window.location.href = "/login";
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("praroha_user", JSON.stringify(updatedUser));
  };

  const addToCart = async (productId, quantity = 1, subscription = false, frequency = null) => {
    const token = localStorage.getItem("praroha_token");
    if (!token) {
      throw new Error("Please login to add items to cart");
    }
    const response = await axios.post(
      `${API}/cart/add`,
      { 
        product_id: productId, 
        quantity,
        subscription,
        subscription_frequency: frequency
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCart(response.data);
    return response.data;
  };

  const updateCart = async (items) => {
    const token = localStorage.getItem("praroha_token");
    if (!token) return;
    const response = await axios.post(
      `${API}/cart`,
      { items },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCart(response.data);
    return response.data;
  };

  const removeFromCart = async (productId) => {
    const token = localStorage.getItem("praroha_token");
    if (!token) return;
    const response = await axios.delete(`${API}/cart/${productId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setCart(response.data);
    return response.data;
  };

  const getToken = () => localStorage.getItem("praroha_token");

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <HelmetProvider>
    <AuthContext.Provider value={{ user, login, register, logout, getToken, updateUser }}>
      <CartContext.Provider value={{ cart, addToCart, updateCart, removeFromCart, fetchCart: () => fetchCart(getToken()) }}>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Admin routes - separate layout, no Header/Footer */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="harvest" element={<AdminHarvest />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="spotlight" element={<AdminSpotlight />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="subscribers" element={<AdminSubscribers />} />
              <Route path="chat-analytics" element={<AdminChatAnalytics />} />
            </Route>

            {/* Main site routes */}
            <Route path="*" element={
              <div className="min-h-screen bg-background flex flex-col">
                <Header />
                <main className="flex-1">
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/about-microgreens" element={<AboutMicrogreensPage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/products/:slug" element={<ProductDetailPage />} />
                    <Route path="/ebook" element={<EbookPage />} />
                    <Route path="/spotlight" element={<SpotlightPage />} />
                    <Route path="/testimonials" element={<TestimonialsPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsConditionsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={user ? <CheckoutPage /> : <Navigate to="/login" />} />
                    <Route path="/login" element={user ? <LoginRedirect /> : <LoginPage />} />
                    <Route path="/register" element={user ? <LoginRedirect /> : <RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/login" />} />
                  </Routes>
                </main>
                <Footer />
                <ChatWidget />
              </div>
            } />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CartContext.Provider>
    </AuthContext.Provider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
